import {
  useWSSnackbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { ICOIExtractionInterface } from "@wingspanhq/shared-files/dist/lib/constants/extractions";
import {
  ISharedFileRequestResponse,
  SharedFileRequestStatus
} from "@wingspanhq/shared-files/dist/lib/interfaces";
import { useDownloadPrivateDocument } from "../../../../../../query/files/mutations";
import { filesService } from "../../../../../../services/files";
import { downloadFileFromBuffer } from "../../../../../../utils/files";
import {
  getRequirementDefinitionDataItemProps,
  getRequirementDefinitionTableCellProps
} from "../../../../../RequirementDefinitions/utils";
import { useRequirementDefaultActions } from "../../../../hooks/useRequirementDefaultActions";
import { selectorIsRequirementReadyToDownloadFile } from "../../../../selectors/selectorIsRequirementReadyToDownloadFile";
import { selectorRequirementEngagementsSize } from "../../../../selectors/selectorRequirementEngagementsSize";
import { selectorSharedFileExtractedFields } from "../../../../selectors/selectorSharedFileExtractedFields";
import {
  CellsConfig,
  ContextItem,
  RequirementContextType,
  RequirementTableCallback
} from "../../../../types";
import { SharedFileDetailsWidgetsContainer } from "../../components/SharedFileDetailsWidgetsContainer";
import { SharedFilePolicies } from "../../components/SharedFilePolicies";
import { getSharedFilePayerRequirementStatus } from "../helpers/getSharedFilePayerRequirementStatus";
import { getSharedFileProgressSteps } from "../helpers/getSharedFileProgressSteps";
import { getSharedFileRequirementTitle } from "../helpers/getSharedFileRequirementTitle";
import { mapContextTypeExtractedFieldsToDataView } from "../helpers/mapContextTypeExtractedFieldsToDataView";
import { renderSharedFileName } from "../helpers/renderSharedFileName";
import { SHARED_FILE_REQUIREMENT_EVENT } from "../utils";
import { payeeSharedFileRenderCardDescription } from "./payeeSharedFileRenderCardDescription";

export const usePayeeSharedFileRequirementConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  ISharedFileRequestResponse | undefined
> => {
  const { openSnackbar } = useWSSnackbar();

  const [downloadDocument] = useDownloadPrivateDocument();

  const requirementDefaultActions = useRequirementDefaultActions(
    RequirementContextType.Payee,
    callback
  );

  const handleRequirementDownload = async (
    requirement: IRequirementResponse,
    fileId: string
  ) => {
    const file = await filesService.member.private.get(fileId);

    await downloadDocument(file.fileId, {
      onSuccess: blob => {
        downloadFileFromBuffer(blob, file?.filename, file.mimetype);
        openSnackbar({
          message: "File downloaded successfully",
          type: "success"
        });
        callback(SHARED_FILE_REQUIREMENT_EVENT.DownloadAsPayee, requirement);
      },
      onError: () => {
        openSnackbar({
          message: "Download failed",
          type: "warning"
        });
      }
    });
  };

  const getDownloadFileAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    sharedFile: ISharedFileRequestResponse | undefined
  ) => {
    const isDocumentReadyToDownload = selectorIsRequirementReadyToDownloadFile(
      requirement,
      sharedFile
    );

    if (isDocumentReadyToDownload) {
      return async () => {
        await handleRequirementDownload(requirement, sharedFile?.fileId!);
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, sharedFile) => {
      return false;
    },
    requirementDetailsProgressWidget: (requirement, context, sharedFile) => {
      return sharedFile
        ? getSharedFileProgressSteps(sharedFile.status, sharedFile.contextType)
        : undefined;
    },
    requirementDetailsWidgets: (requirement, context, sharedFile) => {
      return sharedFile ? (
        <SharedFileDetailsWidgetsContainer
          sharedFileRequestId={sharedFile.sharedFileRequestId}
          contextType={RequirementContextType.Payee}
          requirement={requirement}
          onSuccess={callback}
        />
      ) : null;
    },
    requirementName: getSharedFileRequirementTitle,
    requirementBasicInformation: (requirement, context, sharedFile) => {
      const status = getSharedFilePayerRequirementStatus(
        requirement,
        context,
        sharedFile
      );

      return [
        getRequirementDefinitionDataItemProps({
          type: requirement.requirementType,
          contextType: sharedFile?.contextType
        }),
        {
          label: "Engagements",
          value: selectorRequirementEngagementsSize(requirement)
        },
        {
          label: {
            text: "Status"
          },
          value: "",
          hideValue: true,
          pill: status.pill,
          secondaryText: status.secondaryText
        }
      ];
    },
    requirementDetails: (requirement, context, sharedFile) => {
      const downloadAction = getDownloadFileAction(
        requirement,
        context,
        sharedFile
      );
      const extractedFields = selectorSharedFileExtractedFields(sharedFile);

      return {
        header: {
          label: {
            text: renderSharedFileName(
              requirement,
              context,
              sharedFile
            ) as string
          }
        },
        data: extractedFields
          ? mapContextTypeExtractedFieldsToDataView(
              extractedFields,
              sharedFile?.contextType
            )
          : [],
        children: payeeSharedFileRenderCardDescription(requirement, sharedFile),
        actions: downloadAction
          ? {
              alignment: "fill",
              buttons: [
                {
                  label: "Download file",
                  onAsyncClick: downloadAction,
                  kind: "Secondary"
                }
              ]
            }
          : undefined
      };
    },
    requirementAdditionalContent: (requirement, context, sharedFile) => {
      const extractedFields = selectorSharedFileExtractedFields(
        sharedFile
      ) as ICOIExtractionInterface;

      if (extractedFields?.policies?.length) {
        return <SharedFilePolicies policies={extractedFields.policies} />;
      }
    },

    requirementStatusAlert: (requirement, contextItem, sharedFile) => {
      if (
        sharedFile?.status === SharedFileRequestStatus.PendingUpload ||
        sharedFile?.status === SharedFileRequestStatus.PendingSubmission
      ) {
        return {
          theme: "info",
          title: "Awaiting contractor",
          children: "Awaiting contractor to complete the requirement.",
          icon: "time"
        };
      }

      return undefined;
    },
    toolbarActions: (requirement, context, sharedFile) => {
      const actions: WSPageToolbarAction[] = [];
      const downloadAction = getDownloadFileAction(
        requirement,
        context,
        sharedFile
      );

      if (downloadAction) {
        actions.push({
          onAsyncClick: downloadAction,
          label: "Download file",
          buttonKind: "Secondary",
          icon: "download",
          buttonIcon: null
        });
      }

      const status = getSharedFilePayerRequirementStatus(
        requirement,
        context,
        sharedFile
      );

      const { extendToolbarActions } = requirementDefaultActions(
        requirement,
        context,
        sharedFile,
        status
      );

      return extendToolbarActions(actions);
    },
    nameCell: (requirement, context, sharedFile) => {
      return {
        text: getSharedFileRequirementTitle(requirement, context, sharedFile)
      };
    },
    typeCell: (requirement, _, sharedFile) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType,
        contextType: sharedFile?.contextType
      }),
    statusCell: (requirement, context, sharedFile) => {
      return getSharedFilePayerRequirementStatus(
        requirement,
        context,
        sharedFile
      );
    }
  };
};
